export type LegalDocId = 'terms' | 'privacy' | 'authorization' | 'ai-notice';
export type LegalSection = { id: string; title: string; body: string[] };
export type LegalDoc = {
  id: LegalDocId;
  title: string;
  nav: string;
  updated: string;
  sections: LegalSection[];
};

export const legalIndex = {
  updatedLabel: '更新日期',
  onThisPage: '本页内容',
  draftNote: '以下文本为验证期草案，正式发布前须经法律顾问审阅定稿。',
  backHome: '返回首页',
};

export const legalDocs: Record<LegalDocId, LegalDoc> = {
  terms: {
    id: 'terms',
    title: '服务条款',
    nav: '服务条款',
    updated: '2026 年 10 月',
    sections: [
      {
        id: 'scope',
        title: '一、服务范围',
        body: [
          '念影中文站提供私人纪念用途的遗像制作：源照片整理、目标人物选择或提取，以及遗像构图输出。',
          '本服务不提供殡葬安排、遗体处理或任何线下殡仪服务。',
        ],
      },
      {
        id: 'account',
        title: '二、账号与下单',
        body: [
          '你须注册并登录后方可选择档位、创建订单。登录标识可为手机号、用户名或邮箱之一。',
          '验证期不提供密码自助找回；忘记密码请联系客服或管理员重置。',
        ],
      },
      {
        id: 'payment',
        title: '三、支付与履约（闲鱼）',
        body: [
          '验证期支付方式为：在闲鱼拍下本站公示的对应档位商品并完成付款，再回本站填写闲鱼订单号，由管理员人工确认后解锁上传与生成。',
          '本站不对接闲鱼自动对账或自动退款；退款或异议请按闲鱼规则并联系管理员处理。',
        ],
      },
      {
        id: 'ai',
        title: '四、AI 生成内容',
        body: [
          '成品由 AI 生成合成技术处理，不是原始照片的忠实复制，可能与本人存在差异。',
          '我们不为成品做身份相似度评分；请你与原图人工核对后确认采用版本。',
        ],
      },
      {
        id: 'authz',
        title: '五、肖像授权',
        body: [
          '上传照片前须单独勾选肖像授权声明，该声明独立于本服务条款，不可合并勾选。',
        ],
      },
      {
        id: 'liability',
        title: '六、责任范围',
        body: [
          '在适用法律允许范围内，我们对单笔订单的赔偿责任以该单实付金额为限。主观不满意不构成自动退款理由。',
        ],
      },
      {
        id: 'changes',
        title: '七、条款变更',
        body: ['本条款如有变更，将在本页更新并标注更新日期。'],
      },
    ],
  },
  privacy: {
    id: 'privacy',
    title: '隐私政策',
    nav: '隐私政策',
    updated: '2026 年 10 月',
    sections: [
      {
        id: 'collect',
        title: '一、我们收集什么',
        body: [
          '为完成制作，我们处理你上传的照片，以及注册登录标识、订单与闲鱼单号等最少必要信息。',
          '我们不收集人脸特征用于身份比对或建立人脸库。',
        ],
      },
      {
        id: 'purpose',
        title: '二、照片用于什么',
        body: [
          '照片仅用于本次遗像制作，不会提供给第三方用于广告、公开展示、模型训练或其他用途。',
        ],
      },
      {
        id: 'storage',
        title: '三、存储位置与期限',
        body: [
          '原图与成品存储于 Cloudflare R2。',
          '未付款订单相关文件目标在 24 小时内清理；已付款订单相关文件与成品目标在 7 天内清理。',
          '清理期限内可申请立即删除；受理后素材链接失效，文件随后由后台清理。',
        ],
      },
      {
        id: 'sharing',
        title: '四、第三方处理',
        body: [
          '仅在完成本次制作所必需范围内，由技术服务方（如图像生成接口）处理照片；不得将照片用于训练、广告或其他用途。',
        ],
      },
      {
        id: 'rights',
        title: '五、你的权利',
        body: ['你可以查询订单进度、下载已交付成品，并要求删除照片与相关信息。'],
      },
    ],
  },
  authorization: {
    id: 'authorization',
    title: '肖像授权说明',
    nav: '肖像授权说明',
    updated: '2026 年 10 月',
    sections: [
      {
        id: 'why',
        title: '一、为什么需要授权',
        body: [
          '遗像制作涉及对他人肖像的处理。未经授权可能侵害肖像权、隐私权或名誉利益。',
        ],
      },
      {
        id: 'who',
        title: '二、谁可以授权',
        body: [
          '照片中人物本人可授权处理自己的肖像。',
          '若该人物已故，由其近亲属授权；若为未成年人，由其父母或监护人授权。',
        ],
      },
      {
        id: 'how',
        title: '三、如何确认',
        body: [
          '提交照片前须单独勾选肖像使用授权声明；该声明独立于服务条款，不可合并确认。',
        ],
      },
      {
        id: 'limits',
        title: '四、授权范围',
        body: [
          '授权仅限家庭纪念用途。公开传播、商业使用或可能误导他人的场景不在范围内。',
          '我们保留在有合理疑问时拒绝制作的权利。',
        ],
      },
    ],
  },
  'ai-notice': {
    id: 'ai-notice',
    title: 'AI 生成内容标识说明',
    nav: 'AI 内容标识',
    updated: '2026 年 10 月',
    sections: [
      {
        id: 'what',
        title: '一、为什么会有标识',
        body: [
          '成品由 AI 生成合成技术处理产生。为便于识别，我们保留相应的人工智能生成内容标识。',
        ],
      },
      {
        id: 'how',
        title: '二、标识形式',
        body: [
          '成品文件可包含与生成方式相关的元数据或交付说明中的文字告知。',
        ],
      },
      {
        id: 'meaning',
        title: '三、标识意味着什么',
        body: [
          '标识表示内容经过 AI 处理，并不表示不可用于家庭纪念。',
          '成品可能与原始照片及被摄者本人存在差异；请与原图人工核对，系统不做身份相似度评分。',
        ],
      },
      {
        id: 'dont',
        title: '四、请不要移除标识',
        body: ['请勿删除、篡改或隐藏生成内容标识，勿用于可能误导他人的场景。'],
      },
    ],
  },
};

export function isLegalDocId(v: string): v is LegalDocId {
  return v === 'terms' || v === 'privacy' || v === 'authorization' || v === 'ai-notice';
}
